import {
  AlarmCall,
  AlarmCallEvent,
  AlarmCallEventAudioPhase,
  AlarmCallEventContentType,
  AlarmCallType,
} from '@x-guard/xgac-types/xgac';

import { uniqBy } from 'lodash';
import { advancedQs } from 'lib';
import { readAll } from 'features/details/chat';
import { joinWavs } from './wav';

type FirstMomentEvent = AlarmCallEvent & { content: { type: AlarmCallEventContentType.FirstMomentRecording } };

export type FirstMomentRecordingFile = {
  recordingId: string;
  /** Changes whenever a chunk is added, so the player only reloads when there is more to hear. */
  version: string;
  blob: Blob;
  /** Seconds of audio per joined chunk, in order. */
  chunkDurations: number[];
  /** When the newest chunk was stored. */
  lastUploadAt: number;
  final: boolean;
};

export type LoadedFirstMomentRecordings = {
  recordings: FirstMomentRecordingFile[];
  keepPolling: boolean;
};

// Chunks can still be uploaded for a while after the call ended (the app retries failed uploads).
const lateUploadGraceMs = 2 * 60 * 1000;

const isPublishedFirstMomentEvent = (event: AlarmCallEvent): event is FirstMomentEvent => (
  event.content.type === AlarmCallEventContentType.FirstMomentRecording
  && event.content.state.phase === AlarmCallEventAudioPhase.Published
);

/** The chunks from the start up to the first missing one: playing past a missing chunk would skip audio. */
const contiguousChunks = (events: FirstMomentEvent[]) => {

  // A chunk stored twice (a retry that got a new idempotency key) must not look like a gap.
  const sorted = uniqBy([...events].sort((a, b) => a.content.chunkIndex - b.content.chunkIndex), (event) => event.content.chunkIndex);
  const firstGap = sorted.findIndex((event, index) => event.content.chunkIndex !== index);

  return firstGap === -1 ? sorted : sorted.slice(0, firstGap);

};

const fetchChunk = async (event: FirstMomentEvent, cache: Map<string, ArrayBuffer>, signal: AbortSignal) => {

  const cached = cache.get(event._id);

  if (cached) return cached;

  const { state } = event.content;

  if (state.phase !== AlarmCallEventAudioPhase.Published || !state.src) throw new Error('Chunk has no audio');

  // Plain fetch: the signed url must not get the api auth header.
  const response = await fetch(state.src, { signal });

  if (!response.ok) throw new Error(`Chunk download failed (${response.status})`);

  const buffer = await response.arrayBuffer();

  cache.set(event._id, buffer);

  return buffer;

};

/**
 * The recording the app made from the alarm button press, per alarm call, with its chunks joined into one
 * gapless file. `cache` keeps downloaded chunks so polling only downloads what is new.
 */
export const loadFirstMomentRecordings = async (
  alarmId: string,
  alarmCreatedAt: string,
  cache: Map<string, ArrayBuffer>,
  signal: AbortSignal,
): Promise<LoadedFirstMomentRecordings> => {

  const calls = (await readAll<AlarmCall>(`/alarms/${alarmId}/calls`, signal))
    .filter((call) => call.type === AlarmCallType.AlarmCall);

  const eventsPerCall = (await Promise.all(calls.map((call) => readAll<AlarmCallEvent>(
    `/alarm-calls/${call._id}/alarm-call-events?${advancedQs({ 'content.type': AlarmCallEventContentType.FirstMomentRecording })}`,
    signal,
  )))).map((callEvents) => callEvents.filter(isPublishedFirstMomentEvent));

  const events = eventsPerCall.flat();
  const recordingIds = [...new Set(events.map(({ content }) => content.recordingId))];

  const results = await Promise.allSettled(recordingIds.map(async (recordingId): Promise<FirstMomentRecordingFile | null> => {

    const chunks = contiguousChunks(events.filter(({ content }) => content.recordingId === recordingId));

    // The first chunk is not published yet: nothing to play.
    if (chunks.length === 0) return null;

    const buffers = await Promise.all(chunks.map((chunk) => fetchChunk(chunk, cache, signal)));

    return {
      recordingId,
      version: chunks.map(({ _id }) => _id).join(),
      ...joinWavs(buffers),
      lastUploadAt: Math.max(...chunks.map(({ createdAt }) => Date.parse(createdAt))),
      final: chunks.some(({ content }) => content.final),
    };

  }));

  const recordings = results.flatMap((result) => (result.status === 'fulfilled' && result.value ? [result.value] : []));
  const failed = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');

  // One broken recording must not hide the others; only when nothing could be loaded the poll counts as failed.
  if (failed && recordings.length === 0) throw failed.reason;

  // An open call (or one that just ended) is still waiting for its last chunk. A brand new alarm may not
  // have its call yet. Only a recording that reaches its final chunk without a gap counts as complete.
  const completeRecordingIds = new Set(recordings.filter(({ final }) => final).map(({ recordingId }) => recordingId));
  const awaitingLastChunk = calls.some((call, index) => (
    (!call.endedAt || Date.now() - Date.parse(call.endedAt) < lateUploadGraceMs)
    && !eventsPerCall[index].some(({ content }) => completeRecordingIds.has(content.recordingId))
  ));
  const awaitingCall = calls.length === 0 && Date.now() - Date.parse(alarmCreatedAt) < lateUploadGraceMs;

  return { recordings, keepPolling: awaitingLastChunk || awaitingCall };

};
